import { toMonthlyPrice } from '@brand-lolly/app/billing/plan-interval/plan-interval.util';
import {
	formatPlanPrice,
	ladderStopLabel
} from '@brand-lolly/app/billing/plan-card/plan-tier-copy';
import {
	PlanEntitlementAlias,
	PlanInterval,
	ProductAlias
} from '@brand-lolly/app/billing/types/billing.enums';
import { CatalogPlan } from '@brand-lolly/app/billing/types/plan.types';

/** One allowance a seat can be bought at, as the invite dialog offers it. */
export interface SeatRungOption {
	planId: number;
	/** "400 credits — $164/mo", the same line the plan card's dial reads. */
	label: string;
	/** The monthly credit allowance, or null when the plan states none. */
	credits: number | null;
	/**
	 * What the seat costs each month at this rung — "$164/mo", or empty on a
	 * rung that costs nothing.
	 *
	 * Carried separately from {@link label} because the invite dialog has to
	 * state a cost on its own line, and there is no seat quote to state instead:
	 * `GET /team/seats/quote` is not routed and `POST /team/invites` answers
	 * `quote: null` until the billing wave lands. This is the catalogue's own
	 * figure for the plan being bought, which is a fact rather than an estimate
	 * — what it does NOT say is the part-period proration, so the dialog's copy
	 * does not claim to.
	 */
	priceLabel: string;
}

/** A plan's monthly credit allowance, in whole credits. */
function allowanceOf(plan: CatalogPlan): number | null {
	return (
		(plan.entitlements ?? []).find(
			row =>
				row.alias === PlanEntitlementAlias.APP_CREDITS_GRANT_PER_MONTH
		)?.cap ?? null
	);
}

/**
 * The rungs a new seat may be bought at: ONE TIER'S LADDER, never a choice of
 * tier.
 *
 * The account holds one tier and every seat is licensed at it — that invariant
 * is what makes a per-seat tier choice meaningless, and offering one would be
 * offering a refusal (`tier_mismatch`). So the tier is an argument here, taken
 * from the account's own subscription, and only the allowance varies.
 *
 * The interval is filtered the way the plans page filters it, and with the same
 * fallback: a tier with nothing at the account's interval keeps whatever rows it
 * does have, rather than producing an empty dial.
 *
 * Sorted by allowance, and a rung stating none sorts last instead of being
 * dropped — a plan the catalogue cannot describe should still be buyable.
 */
export function buildSeatRungs(
	plans: CatalogPlan[],
	tierLevel: number,
	interval: PlanInterval
): SeatRungOption[] {
	const inTier = plans.filter(
		plan =>
			plan.tier_level === tierLevel &&
			plan.alias.startsWith(ProductAlias.APPLICATION) &&
			plan.is_active !== false
	);
	const atInterval = inTier.filter(plan => plan.interval === interval);
	const rungs = (atInterval.length > 0 ? atInterval : inTier)
		.slice()
		.sort(
			(a, b) =>
				(allowanceOf(a) ?? Number.MAX_SAFE_INTEGER) -
				(allowanceOf(b) ?? Number.MAX_SAFE_INTEGER)
		);

	return rungs.map(plan => {
		const credits = allowanceOf(plan);
		const monthly = parseFloat(toMonthlyPrice(plan));
		return {
			planId: plan.id,
			label: ladderStopLabel(plan, credits),
			credits,
			priceLabel:
				isNaN(monthly) || monthly <= 0
					? ''
					: `${formatPlanPrice(toMonthlyPrice(plan))}/mo`
		};
	});
}

/** Every application plan in the catalogue, whatever product id it sits under. */
export function flattenApplicationPlans(
	byProduct: Record<number, CatalogPlan[]>
): CatalogPlan[] {
	const all: CatalogPlan[] = [];
	for (const key of Object.keys(byProduct)) {
		all.push(...(byProduct[Number(key)] ?? []));
	}
	return all.filter(plan => plan.alias.startsWith(ProductAlias.APPLICATION));
}
